import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/ui/components/Button';
import { ToggleSwitchCard } from '@/ui/components/ToggleSwitchCard';
import { Icon } from '@/ui/icons/Icon';
import { checkMarkButton } from '@/assets/images/emojis';
import { useOnboarding } from '@/features/onboarding';
import { useOfferRequest } from '@/features/offer/hooks/useOfferRequest';
import {
  NotificationCategory,
  NotificationType,
  useNotifications,
} from '@/features/notifications';
import { routes } from '@/routes/routes';
import { useError } from '@/providers/Error';
import { genericMessages } from '@/constants/errorMessages';
import { useTerms } from '../../hooks/useTerms';
import styles from './AnafConsentPage.module.scss';

// ANAF consent only, once KYC is valid; BC consent was given before the BC
// score. The marketing opt-in is asked here since the combined terms page
// that holds it is skipped.
export function AnafConsentPage() {
  const navigate = useNavigate();
  const { setError } = useError();
  const { acceptPartnerAnafTerms, isLoading: isTermsLoading } = useTerms();
  const { requestOffer, isLoading: isOfferLoading } = useOfferRequest();
  const { getOnboardingState } = useOnboarding();
  const { isUpdating: isUpdatingNotifications, updateNotificationSettings } =
    useNotifications();
  const [isAccepted, setIsAccepted] = useState(false);
  const [isNotificationsAccepted, setIsNotificationsAccepted] = useState(false);

  const isLoading = isTermsLoading || isOfferLoading || isUpdatingNotifications;

  const handleContinue = async () => {
    try {
      await acceptPartnerAnafTerms();
      await requestOffer();

      if (!isNotificationsAccepted) {
        await updateNotificationSettings({
          category: NotificationCategory.Email,
          type: NotificationType.Marketing,
          isEnabled: false,
        });
      }

      await getOnboardingState();
      navigate(routes.wizard.base);
    } catch {
      setError(genericMessages.unexpected);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.emojiContainer}>
            <img
              src={checkMarkButton}
              alt="Confirm agreement"
              className={styles.emoji}
            />
          </div>
          <div className={styles.textContainer}>
            <h1 className={styles.title}>Confirmă venitul tău</h1>
            <p className={styles.subtitle}>
              Avem nevoie de acordul tău pentru a verifica veniturile tale la
              ANAF și a-ți calcula oferta finală.
            </p>
          </div>
        </div>

        <div className={styles.termsSection}>
          <div className={styles.termsItem}>
            <ToggleSwitchCard
              checked={isAccepted}
              label="Sunt de acord cu interogarea la ANAF"
              onChange={setIsAccepted}
              testId="anaf"
            />
            <div className={styles.linkContainer}>
              <Link to={routes.terms.anaf} className={styles.link}>
                Termeni și condiții ANAF
              </Link>
            </div>
          </div>

          <ToggleSwitchCard
            checked={isNotificationsAccepted}
            label="Confirm că sunt de acord să primesc notificări referitoare la campanii promoționale și oferte de credit"
            onChange={setIsNotificationsAccepted}
            testId="notifications"
          />
        </div>
      </div>

      <div className={styles.buttonContainer}>
        <Button
          trackingId="accept_anaf_terms"
          variant="filled"
          text="Continuă"
          className={styles.button}
          onClick={handleContinue}
          disabled={!isAccepted || isLoading}
          loading={isLoading}
          rightIcon={<Icon name="arrow-right" />}
        />
      </div>
    </div>
  );
}

export default AnafConsentPage;
